import * as React from 'react';
import styled from 'styled-components';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import VideocamOffIcon from '@material-ui/icons/VideocamOff';
import ModalDefaultDraggable from '../../molecules/ModalDefaultDraggable';

// Inspectors can attach short clips (InspectionComment.file allows mp4/mov),
// so an attachment is not necessarily an image.
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm', 'ogv', 'ogg', 'qt'];

export const isVideoUrl = (url?: string) => {
  if (!url || typeof url !== 'string') return false;
  const ext = url
    .split(/[?#]/)[0]
    .split('.')
    .pop()
    ?.toLowerCase();
  return !!ext && VIDEO_EXTENSIONS.includes(ext);
};

// The API serialises media as an absolute URL built from the backend's own
// host (e.g. http://127.0.0.1:8080/media/...). In dev that is a different
// origin from the app on :5050, so the request skips dev-server.js' /media/
// proxy and any cross-origin probe is blocked by CORS. Deployed builds serve
// the API and /media/ from the same host, so making the path relative there is
// a no-op. Left untouched unless it really is a /media/ path on another origin.
export const toSameOriginMedia = (url?: string) => {
  if (!url || typeof url !== 'string') return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (
      parsed.origin !== window.location.origin &&
      parsed.pathname.startsWith('/media/')
    ) {
      return `${parsed.pathname}${parsed.search}`;
    }
    return url;
  } catch {
    return url;
  }
};

interface AttachmentProps {
  url: string;
  filename?: string;
  onClick: () => void;
}

// Inline thumbnail tile for the comment list. Falls back to a "video
// unavailable" tile when the file can't be reached, so a missing upload does
// not just render as an empty box.
export const VideoAttachment = ({ url, filename, onClick }: AttachmentProps) => {
  const [broken, setBroken] = React.useState(false);

  return (
    <VideoPreview
      onClick={onClick}
      title={filename || 'Play video'}
      $broken={broken}
    >
      {broken ? (
        <MissingTile>
          <VideocamOffIcon fontSize="inherit" />
          <MissingLabel>Video unavailable</MissingLabel>
          {filename && <MissingName>{filename}</MissingName>}
        </MissingTile>
      ) : (
        <>
          {/* #t=0.1 makes browsers paint a real frame as the poster */}
          <VideoThumb
            src={`${url}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            onError={() => setBroken(true)}
          />
          <PlayBadge>
            <PlayArrowIcon fontSize="inherit" />
          </PlayBadge>
        </>
      )}
    </VideoPreview>
  );
};

interface ModalProps {
  url: string;
  onClose: () => void;
}

export const VideoPreviewModal = ({ url, onClose }: ModalProps) => {
  const [videoError, setVideoError] = React.useState<string | null>(null);

  // <video> fires a bare `error` event for every failure, so ask the server
  // what actually happened -- a missing upload and an undecodable codec need
  // very different messages.
  const handleVideoError = async (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const src = e.currentTarget.currentSrc || e.currentTarget.src;
    const code = e.currentTarget.error?.code;
    const unsupported =
      'This video cannot be played in the browser (unsupported format or codec). Use "Open in new tab" to download it.';
    try {
      const res = await fetch(src, { method: 'HEAD' });
      if (res.status === 404) {
        setVideoError(
          'This video is no longer available on the server (404). The comment still references the file, but the upload is missing.',
        );
      } else if (!res.ok) {
        setVideoError(
          `This video could not be loaded (server returned ${res.status}).`,
        );
      } else {
        setVideoError(unsupported);
      }
    } catch {
      // The probe itself failed (offline, or blocked cross-origin), so the
      // status is unknown -- fall back to what <video> reported.
      setVideoError(
        code === 3
          ? unsupported
          : 'This video could not be loaded. The file may be missing or unreachable.',
      );
    }
  };

  return (
    <ModalDefaultDraggable
      title="Video Preview"
      onClose={onClose}
      centered
      wide
      zIndex={1400}
    >
      <VideoModalContent>
        <VideoToolbar>
          <OpenInNewLink href={url} target="_blank" rel="noreferrer">
            <OpenInNewIcon fontSize="inherit" />
            Open in new tab
          </OpenInNewLink>
        </VideoToolbar>
        <PreviewVideoWrapper>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <PreviewVideo
            src={url}
            controls
            autoPlay
            playsInline
            onError={handleVideoError}
          />
        </PreviewVideoWrapper>
        {videoError && <VideoError>{videoError}</VideoError>}
      </VideoModalContent>
    </ModalDefaultDraggable>
  );
};

const VideoPreview = styled.div<{ $broken?: boolean }>`
  position: relative;
  width: 180px;
  height: 120px;
  margin-top: var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid
    ${({ $broken }) => ($broken ? 'var(--neutral-300)' : 'var(--neutral-200)')};
  background: ${({ $broken }) =>
    $broken ? 'var(--neutral-100)' : 'var(--neutral-900)'};
  overflow: hidden;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const MissingTile = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0 var(--space-2);
  box-sizing: border-box;
  text-align: center;
  color: var(--neutral-500);
  font-size: 26px;
`;

const MissingLabel = styled.span`
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--neutral-600);
`;

const MissingName = styled.span`
  font-size: var(--fs-xs, 0.6875rem);
  color: var(--neutral-500);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VideoThumb = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
  background: var(--neutral-900);
`;

const PlayBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 44px;
  height: 44px;
  border-radius: var(--radius-full);
  background: rgba(0, 0, 0, 0.55);
  color: var(--neutral-0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  pointer-events: none;

  ${VideoPreview}:hover & {
    background: var(--primary-600);
  }
`;

const VideoModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: var(--space-2);
`;

const VideoToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--neutral-200);
`;

const OpenInNewLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--primary-600);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const PreviewVideoWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--neutral-900);
  border-radius: var(--radius-sm);
`;

const PreviewVideo = styled.video`
  max-width: 100%;
  max-height: 70vh;
  display: block;
`;

const VideoError = styled.p`
  margin: 0;
  text-align: center;
  font-size: var(--fs-sm);
  color: var(--danger-500);
`;
